import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import {
	Loader2,
	Send,
	FileText,
	Check,
	XCircle,
	CalendarX,
	Eye,
	Link2,
	FileSignature,
	AlertTriangle,
	Undo2,
	ArrowUpRight,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "../ui/button";
import ItensDaVenda from "../ItensDaVenda";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { copiarTexto } from "../../helpers/copiarTexto";
import { gerarProposta } from "../../helpers/gerarProposta";
import usePermissions from "../../hooks/usePermissions";
import useModules from "../../hooks/useModules";
import LinhaDoTempoDaVenda from "../LinhaDoTempoDaVenda";

export const SITUACAO_DO_ORCAMENTO = {
	draft: { rotulo: "Rascunho", classe: "text-muted-foreground" },
	sent: { rotulo: "Enviado, sem resposta", classe: "text-primary" },
	viewed: { rotulo: "Aberto pelo cliente", classe: "text-sky-600" },
	accepted: { rotulo: "Aceito", classe: "text-emerald-600" },
	rejected: { rotulo: "Recusado", classe: "text-destructive" },
	expired: { rotulo: "Vencido", classe: "text-muted-foreground" },
};

// O endereço que o cliente abre. Mora no mesmo domínio da aplicação — é a
// mesma origem que serve esta tela.
export const linkPublico = (orcamento) =>
	orcamento?.publicToken
		? `${window.location.origin}/orcamento/${orcamento.publicToken}`
		: null;

export const copiarLinkPublico = async (orcamento) => {
	const link = linkPublico(orcamento);

	if (!link) {
		toast.error("Este orçamento ainda não tem link. Recarregue a tela.");
		return;
	}

	await copiarTexto(link, "Link copiado. É só mandar para o cliente.");
};

// Data e hora: "visto ontem às 21h" e "visto hoje de manhã" mudam o que quem
// vende faz em seguida.
export const formatarDataHora = (valor) =>
	valor ? new Date(valor).toLocaleString("pt-BR") : null;

// Itens do cartão: é daqui que sai o valor do negócio, no lugar de um número
// digitado na mão. Aceitar o orçamento escreve o total no valor do cartão.
//
// Serve a dois lugares: o cartão do Kanban (que sabe o `cardId` e pega o
// orçamento mais recente dele) e a tela de Orçamentos (que já sabe qual
// orçamento abrir e passa `quoteId`). É o mesmo trabalho — separar em dois
// componentes seria manter duas vezes a mesma regra de item, desconto e envio.
const OrcamentoDoCartao = ({
	cardId,
	contactId,
	quoteId,
	ticketId,
	onValorMudou,
	onMudou,
	// Painel de 360px usa a linha do tempo compacta; os modais (Kanban e
	// Orçamentos) têm largura para o rótulo sob cada ponto.
	linhaCompacta = false,
}) => {
	const { temPermissao } = usePermissions();
	// Proposta é de outro módulo (`propostas`, ModuleKeys.ts): sem ele
	// contratado o botão some, em vez de levar a pessoa a um 403.
	const { temModulo } = useModules();
	// Virar proposta é trocar o formato da venda (Fase 5): edição do
	// orçamento, com o módulo de propostas.
	const podePropor = temPermissao("quotes:manage") && temModulo("propostas");
	const navigate = useNavigate();
	// Sem a permissão o orçamento continua visível — quem atende precisa saber
	// o que foi orçado —, mas sem os campos e botões que o backend recusaria.
	const podeEditar = temPermissao("quotes:manage");

	const [orcamento, setOrcamento] = useState(null);
	const [carregando, setCarregando] = useState(false);
	const [ocupado, setOcupado] = useState(false);
	const carregar = useCallback(async () => {
		if (!cardId && !quoteId) return;

		setCarregando(true);
		try {
			if (quoteId) {
				const { data } = await api.get(`/quotes/${quoteId}`);
				setOrcamento(data);
			} else {
				const { data } = await api.get("/quotes", { params: { cardId } });
				// O mais recente: um cartão pode ter tido orçamento recusado antes.
				setOrcamento(data.quotes?.[0] || null);
			}
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, [cardId, quoteId]);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const criar = async () => {
		setOcupado(true);
		try {
			const { data } = await api.post("/quotes", { contactId, cardId });
			setOrcamento(data);
		} catch (err) {
			toastError(err);
		}
		setOcupado(false);
	};

	const enviar = async () => {
		setOcupado(true);
		try {
			// `ticketId` chega quando o envio parte da própria conversa: é ele que
			// diz em qual atendimento o PDF deve ser registrado.
			const { data } = await api.post(`/quotes/${orcamento.id}/send`, {
				ticketId,
			});
			setOrcamento(data.quote);
			onMudou?.();
			toast.success("Orçamento enviado pela conversa.");
		} catch (err) {
			toastError(err);
		}
		setOcupado(false);
	};

	const mudarSituacao = async (status) => {
		setOcupado(true);
		try {
			const { data } = await api.put(`/quotes/${orcamento.id}/status`, {
				status,
			});
			setOrcamento(data);
			onMudou?.();
			// Aceitar escreve o total no valor do cartão; quem abriu o modal
			// precisa ver o campo mudar sem recarregar a página.
			if (status === "accepted") onValorMudou?.(data.total);
		} catch (err) {
			toastError(err);
		}
		setOcupado(false);
	};

	if (!cardId && !quoteId) {
		return (
			<p className="text-xs text-muted-foreground">
				Salve o cartão antes de montar o orçamento.
			</p>
		);
	}

	if (carregando) {
		return (
			<div className="flex justify-center py-6">
				<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (!orcamento) {
		return (
			<div className="space-y-2 rounded-lg border border-dashed p-4 text-center">
				<p className="text-xs text-muted-foreground">
					Sem orçamento. Com itens, o valor do cartão deixa de ser digitado na
					mão e passa a ser a soma do que foi vendido.
				</p>
				<Button
					size="sm"
					variant="outline"
					onClick={criar}
					disabled={ocupado || !contactId || !podeEditar}
				>
					<FileText className="h-4 w-4" />
					Criar orçamento
				</Button>
				{!contactId && (
					<p className="text-xs text-destructive">
						Escolha o cliente primeiro — o orçamento é para alguém.
					</p>
				)}
			</div>
		);
	}

	const rascunho = orcamento.status === "draft";

	// Rascunho é o estado do orçamento; editável é rascunho mais permissão.
	const editavel = rascunho && podeEditar;

	// Aberto pelo cliente ainda é orçamento sem resposta: as saídas são as
	// mesmas de "enviado".
	const esperandoResposta =
		["sent", "viewed"].includes(orcamento.status) && podeEditar;

	return (
		<div className="space-y-3 rounded-lg border p-3">
			<div className="flex items-center justify-between">
				<span className="text-sm font-medium">
					Orçamento nº {orcamento.number}
					{orcamento.order && (
						<span className="ml-2 text-xs font-normal text-muted-foreground">
							Pedido nº {orcamento.order.number}
						</span>
					)}
				</span>
				{/* A página da venda tem a história inteira: pagamento, estoque,
				    cliente, linha do tempo. */}
				<Link
					to={`/vendas/${orcamento.id}`}
					className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
				>
					Abrir venda
					<ArrowUpRight className="h-3.5 w-3.5" />
				</Link>
			</div>

			{/* A história inteira num desenho só — o mesmo do painel da conversa
			    e da tela de Pedidos. Substitui o chip de situação solto. */}
			<LinhaDoTempoDaVenda
				compacto={linhaCompacta}
				situacao={orcamento.status}
				pedido={orcamento.order}
			/>

			{/* O "sim" do cliente é o momento mais valioso do fluxo e não pode
			    terminar em nada: sem pedido ligado, o aceite era um beco — só
			    mudava a cor do texto. */}
			{/* O "por que perdemos", escrito pelo próprio cliente ao recusar no
			    link. Sem mostrar aqui, a resposta mais útil do orçamento ficaria
			    só no banco. */}
			{orcamento.status === "rejected" && orcamento.rejectionReason && (
				<p className="rounded-lg border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
					Motivo da recusa: {orcamento.rejectionReason}
				</p>
			)}

			{/* O canal recusou o PDF: "Enviado, sem resposta" seria mentira. */}
			{orcamento.envioFalhou && (
				<p className="flex items-center gap-1.5 rounded-lg border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
					<AlertTriangle className="h-3.5 w-3.5 shrink-0" />
					O PDF não foi entregue no canal. Veja a conversa e reenvie.
				</p>
			)}

			{orcamento.viewedAt && (
				<p className="flex items-center gap-1.5 text-xs text-sky-600">
					<Eye className="h-3.5 w-3.5 shrink-0" />
					Visto em {formatarDataHora(orcamento.viewedAt)}
				</p>
			)}

			<ItensDaVenda
				orcamento={orcamento}
				onOrcamento={setOrcamento}
				editavel={editavel}
				variante={linhaCompacta ? "compacto" : "linha"}
			/>

			<div className="flex flex-wrap gap-2">
				{editavel && (
					<Button
						size="sm"
						onClick={enviar}
						disabled={
							ocupado ||
							!(orcamento.items || []).length ||
							(orcamento.items || []).some((i) => !(Number(i.unitPrice) > 0))
						}
					>
						{ocupado ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Send className="h-4 w-4" />
						)}
						Enviar pela conversa
					</Button>
				)}
				{!rascunho && (
					<Button
						size="sm"
						variant="outline"
						onClick={() => copiarLinkPublico(orcamento)}
					>
						<Link2 className="h-4 w-4" />
						Copiar link
					</Button>
				)}
				{/* A proposta nasce do orçamento: cliente, itens e valor já estão
				    aqui, e redigitar tudo na outra tela é erro esperando acontecer. */}
				{podePropor &&
					orcamento.status === "draft" &&
					orcamento.format !== "proposal" && (
						<Button
							size="sm"
							variant="outline"
							disabled={ocupado}
							onClick={async () => {
								const venda = await gerarProposta({ quoteId: orcamento.id });
								if (venda) navigate(`/vendas/${venda.id}`);
							}}
						>
							<FileSignature className="h-4 w-4" />
							Virar proposta completa
						</Button>
					)}
				{esperandoResposta && (
					<>
						<Button
							size="sm"
							variant="outline"
							onClick={() => mudarSituacao("accepted")}
							disabled={ocupado}
						>
							<Check className="h-4 w-4" />
							Cliente aceitou
						</Button>
						<Button
							size="sm"
							variant="outline"
							onClick={() => mudarSituacao("rejected")}
							disabled={ocupado}
						>
							<XCircle className="h-4 w-4" />
							Recusou
						</Button>
						{/* Sem resposta até a validade: nem aceito nem recusado. Sem
						    esta saída, o orçamento morto fica para sempre entre os que
						    esperam resposta e a lista de pendências mente. */}
						<Button
							size="sm"
							variant="outline"
							onClick={() => mudarSituacao("expired")}
							disabled={ocupado}
						>
							<CalendarX className="h-4 w-4" />
							Venceu sem resposta
						</Button>
						{/* Itens e valores só mudam no rascunho. Reenviar depois zera
						    o "visto", então o cliente recebe a versão nova como nova. */}
						<Button
							size="sm"
							variant="outline"
							onClick={() => mudarSituacao("draft")}
							disabled={ocupado}
						>
							<Undo2 className="h-4 w-4" />
							Voltar para rascunho
						</Button>
					</>
				)}
			</div>

			{editavel && (
				<p className="text-xs leading-relaxed text-muted-foreground">
					Enviar gera o PDF e manda como documento na conversa do cliente. O
					preço fica congelado no que está aqui — mudar a tabela depois não
					reescreve este orçamento. O pedido só nasce quando o cliente
					aceitar.
				</p>
			)}
		</div>
	);
};

export default OrcamentoDoCartao;
